import moment from 'moment'

import { localStorageFeatureToggle } from '../../utils/feature-toggle-utils'

import type { Collaborator, ConversationTimelineItem } from './types'

// SECURITY: callers of formatMessageText pipe the result into
// dangerouslySetInnerHTML. Any user-controlled data (collaborator
// firstName/lastName/username) interpolated into the returned HTML must
// be escaped at the injection point — otherwise a user who sets their
// name to e.g. `<img onerror=alert(document.cookie) src=x>` triggers
// stored XSS in every external reviewer's browser. DOMPurify is run
// upstream on the message body, but the @[userId] expansion happens
// AFTER that, so unsanitised collaborator data would slip past the
// sanitiser. Escape here so the function is safe regardless of caller
// ordering.
const escapeHtml = (raw: string): string =>
  raw.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&':
        return '&amp;'
      case '<':
        return '&lt;'
      case '>':
        return '&gt;'
      case '"':
        return '&quot;'
      case "'":
        return '&#39;'
      default:
        return ch
    }
  })

export const formatMessageText = (
  messageText: string,
  collaborators: Collaborator[],
  textOnly = false
): string => {
  // eslint-disable-next-line no-useless-escape
  const regex = /@[\[]\w+[\]]/gm
  return messageText.replace(regex, (match) => {
    const collaborator = collaborators.find((collab: Collaborator) =>
      match.includes(collab._id)
    )
    const name = collaborator ? `${collaborator.username || ''}` : 'unknown'
    if (!textOnly && localStorageFeatureToggle.get('CaptionV2')) {
      const displayName = collaborator
        ? `${collaborator.firstName || ''} ${collaborator.lastName || ''}`.trim()
        : 'unknown'
      return `<span class="text-blue-500 font-semibold">${escapeHtml(displayName)}</span>`
    }
    return textOnly
      ? `@${name}`
      : `<span class="text-[#4f31ff]">@${escapeHtml(name)}</span>`
  })
}

export const sortMessagesByDate = (
  messages: ConversationTimelineItem[]
): ConversationTimelineItem[] => {
  return [...messages].sort(
    (message, nextMessage) =>
      moment(new Date(message?.createdAt)).unix() -
      moment(new Date(nextMessage?.createdAt)).unix()
  )
}

export const getLastStatusChangeMessage = (
  messages?: ConversationTimelineItem[]
): ConversationTimelineItem | null => {
  if (!messages || messages.length === 0) {
    return null
  }

  const statusChangeMessages = messages.filter(
    (msg) => msg.variant === 'statusChange'
  )
  if (statusChangeMessages.length === 0) {
    return null
  }
  const sortedMessages = sortMessagesByDate(statusChangeMessages)

  return sortedMessages[sortedMessages.length - 1] || null
}

export const checkIsReplied = (
  status?: string,
  messages?: ConversationTimelineItem[]
): boolean => {
  if (status !== 'requestChange' || !messages || messages.length === 0) {
    return false
  }

  const lastMessage = getLastStatusChangeMessage(messages)

  return Boolean(
    lastMessage &&
    lastMessage.isMessageOwner &&
    lastMessage.comment &&
    lastMessage.comment.trim().length > 0
  )
}

export const scrollToBottomOfConversation = (): void => {
  const conversationWrapElem = document.getElementById('conversation_wrap')
  if (conversationWrapElem) {
    conversationWrapElem.scrollTop = conversationWrapElem.scrollHeight
  }
}

export const toCapitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}
